import React, { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, Grid, Text, Spinner, Flex } from '@chakra-ui/react';
// Highcharts renders to SVG/canvas, so it needs a real colour — CSS variables
// are not resolved inside chart option objects.
import { PRIMARY_BG, primaryAlpha } from '../../../theme/brand';

const ExecutionCharts = ({ cohortId }) => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem('api-key'),
        "x-access-token": localStorage.getItem('customer-portal'),
    });

    const fetchChartData = async () => {
        try {
            setLoading(true);
            const url = `${process.env.REACT_APP_API_URL}/cohorts/${cohortId}/report/summary`;

            const response = await fetch(url, {
                method: 'GET',
                headers: getApiHeaders(),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch chart data');
            }

            const result = await response.json();

            if (result.success) {
                setChartData(result.data);
            }
        } catch (err) {
            setError(err.message);
            console.error('Error fetching chart data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (cohortId) {
            fetchChartData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortId]);

    // Contact Changes Over Time Chart (Column + Line Mix)
    const getContactChangesOptions = () => {
        if (!chartData || !chartData.net_change_per_exec) return null;

        const dates = chartData.net_change_per_exec.map(item =>
            new Date(item.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        );
        const added = chartData.net_change_per_exec.map(item => item.added);
        const removed = chartData.net_change_per_exec.map(item => item.removed);
        const retained = chartData.net_change_per_exec.map(item => item.retained);

        return {
            chart: {
                type: 'column',
                height: 450,
            },
            title: {
                text: null,
            },
            xAxis: {
                categories: dates,
                labels: {
                    rotation: -45,
                    style: {
                        fontSize: '11px'
                    }
                },
                gridLineWidth: 1,
                gridLineDashStyle: 'Dash',
                gridLineColor: 'rgba(0,0,0,0.1)',
            },
            yAxis: {
                title: {
                    text: null
                },
                min: 0,
                gridLineDashStyle: 'Dash',
                gridLineColor: 'rgba(0,0,0,0.1)',
                lineWidth: 1,
            },
            legend: {
                align: 'center',
                verticalAlign: 'bottom',
                layout: 'horizontal',
            },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    borderWidth: 0,
                    pointPadding: 0.1,
                    groupPadding: 0.1,
                }
            },
            tooltip: {
                shared: true
            },
            series: [
                {
                    name: 'Added',
                    type: 'column',
                    data: added,
                    color: '#22C55E',
                    stack: 'changes'
                },
                {
                    name: 'Removed',
                    type: 'column',
                    data: removed,
                    color: '#EF4444',
                    stack: 'changes'
                },
                {
                    name: 'Retained',
                    data: retained,
                    color: PRIMARY_BG,
                    lineWidth: 2,
                    fillColor: {
                        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                        stops: [
                            [0, primaryAlpha(0.3)], // top gradient (50% opacity)
                            [1, primaryAlpha(0.3)],   // bottom fade out
                        ],
                    },
                    marker: {
                        enabled: true,
                        radius: 4,
                        symbol: 'circle',
                        fillColor: PRIMARY_BG
                    },
                    type: 'areaspline',
                    yAxis: 0
                }
            ],
            credits: {
                enabled: false
            }
        };
    };

    // Total Contacts Trend Chart
    const getTotalContactsOptions = () => {
        if (!chartData || !chartData.total_contacts_trend) return null;

        const dates = chartData.total_contacts_trend.map(item =>
            new Date(item.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        );
        const totals = chartData.total_contacts_trend.map(item => item.total_contacts);

        return {
            chart: {
                type: 'line',
                height: 450,
            },
            title: {
                text: null,
            },
            xAxis: {
                categories: dates,
                labels: {
                    rotation: -45,
                    style: {
                        fontSize: '11px'
                    }
                },
                gridLineWidth: 1,
                gridLineDashStyle: 'Dash',
                gridLineColor: 'rgba(0,0,0,0.1)',
            },
            yAxis: {
                title: {
                    text: null
                },
                min: 0,
                gridLineDashStyle: 'Dash',
                gridLineColor: 'rgba(0,0,0,0.1)',
                lineWidth: 1,
            },
            legend: {
                align: 'center',
                verticalAlign: 'bottom',
            },
            plotOptions: {
                line: {
                    marker: {
                        enabled: true,
                        radius: 4,
                        symbol: 'circle'
                    }
                }
            },
            series: [{
                name: 'Total Contacts',
                data: totals,
                color: PRIMARY_BG,
                lineWidth: 2,
                fillColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: [
                        [0, primaryAlpha(0.5)], // top gradient (50% opacity)
                        [1, primaryAlpha(0.5)],   // bottom fade out
                    ],
                },
            }],
            credits: {
                enabled: false
            }
        };
    };

    // Net Change Per Execution Chart
    const getNetChangeOptions = () => {
        if (!chartData || !chartData.net_change_per_exec) return null;

        const dates = chartData.net_change_per_exec.map(item =>
            new Date(item.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        );
        const netChanges = chartData.net_change_per_exec.map(item => item.net);

        return {
            chart: {
                type: 'column',
                height: 450,
            },
            title: {
                text: null,
            },
            xAxis: {
                categories: dates,
                labels: {
                    rotation: -45,
                    style: {
                        fontSize: '11px'
                    }
                },
                gridLineWidth: 1,
                gridLineDashStyle: 'Dash',
                gridLineColor: 'rgba(0,0,0,0.1)',
            },
            yAxis: {
                title: {
                    text: null
                },
                plotLines: [{
                    value: 0,
                    color: '#000',
                    width: 1,
                    zIndex: 3
                }],
                gridLineDashStyle: 'Dash',
                gridLineColor: 'rgba(0,0,0,0.1)',
                lineWidth: 1,
            },
            legend: {
                align: 'center',
                verticalAlign: 'bottom',
            },
            plotOptions: {
                column: {
                    borderWidth: 0,
                    pointPadding: 0.1,
                    groupPadding: 0.1,
                }
            },
            tooltip: {
                formatter: function () {
                    return `<b>${this.x}</b><br/>Net Change: ${this.y}`;
                }
            },
            series: [{
                name: 'Net Change',
                data: netChanges.map(value => ({
                    y: value,
                    color: value >= 0 ? '#22C55E' : '#EF4444'
                })),
                showInLegend: true,
                color: '#22C55E'
            }],
            credits: {
                enabled: false
            }
        };
    };

    if (loading) {
        return (
            <Flex justify="center" align="center" minH="400px">
                <Spinner size="xl" color="blue.500" />
            </Flex>
        );
    }

    if (error) {
        return (
            <Box bg="red.50" p={4} borderRadius="md">
                <Text color="red.600">Error loading charts: {error}</Text>
            </Box>
        );
    }

    return (
        <Box>
            <Grid templateColumns="repeat(2, 1fr)" gap={6} mb={6}>
                {/* Contact Changes Over Time */}
                <Box className="box-style-card" p={4}>
                    <Box mb={4}>
                        <Text fontSize="md" fontWeight="semibold" color="gray.700" mb={1}>
                            Contact Changes Over Time
                        </Text>
                        <Text fontSize="sm" color="#64748b">
                            Contact composition: Retained, Added, and Removed per execution
                        </Text>
                    </Box>
                    {chartData && getContactChangesOptions() && (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={getContactChangesOptions()}
                        />
                    )}
                </Box>

                {/* Total Contacts Trend */}
                <Box className="box-style-card" p={4}>
                    <Box mb={4}>
                        <Text fontSize="md" fontWeight="semibold" color="gray.700" mb={1}>
                            Total Contacts Trend
                        </Text>
                        <Text fontSize="sm" color="#64748b">
                            Segment size evolution over time
                        </Text>
                    </Box>
                    {chartData && getTotalContactsOptions() && (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={getTotalContactsOptions()}
                        />
                    )}
                </Box>
            </Grid>

            {/* Net Change Per Execution */}
            <Box className="box-style-card" p={4}>
                <Box mb={4}>
                    <Text fontSize="md" fontWeight="semibold" color="gray.700" mb={1}>
                        Net Change Per Execution
                    </Text>
                    <Text fontSize="sm" color="#64748b">
                        Positive values indicate growth, negative indicate shrinkage
                    </Text>
                </Box>
                {chartData && getNetChangeOptions() && (
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={getNetChangeOptions()}
                    />
                )}
            </Box>
        </Box>
    );
};

export default ExecutionCharts;